import React, { useState, useEffect } from 'react';
import {
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Container,
    Row,
    Col,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { login, clearTwoFactorRequirement, clearError, logout } from '../../slices/auth.slice';
import TwoFactorVerification from '../../components/TwoFactorVerification';
import ToggleSwitch from '../../components/ToggleSwitch';
import PinScreen from '../../components/PinScreen';
import PasswordInput from '../../components/PasswordInput';
import ApiHealthStatus from '../../components/ApiHealthStatus';
import { useApiHealth } from '../../hooks/useApiHealth';
import { isLoginBlocked } from '../../services/apiHealth';
import './Login.scss';
import { toast } from 'react-toastify';
import { errorToast } from '../../utils/errorToast';
import api, {getDeviceName} from '../../api';
import Package from '../../../package.json';


const ROLES_WITHOUT_PIN = ['root', 'bac-user', 'bac-admin'];

const detectPinReturnMode = () => {
    const refreshTokenLs = localStorage.getItem('refreshToken');
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    const hasPinCode = localStorage.getItem('hasPinCode') === 'true';
    const role = localStorage.getItem('role');
    return Boolean(
        refreshTokenLs &&
        rememberedUsername &&
        hasPinCode &&
        role &&
        !ROLES_WITHOUT_PIN.includes(role)
    );
};

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { requiresTwoFactor, pendingLogin, isLoading, error } = useSelector(state => state.auth);
    const { health, isChecking, recheck } = useApiHealth();

    // The API is unreachable (down, or refusing this origin) — a login attempt can only
    // fail with a network error, so block it instead of showing a bogus "wrong credentials".
    const serviceUnavailable = isLoginBlocked(health.status);

    const [username, setUsername] = useState('');
    const [loginError, setLoginError] = useState('');

    // Source the credential error from the persistent Redux store so it survives
    // any re-render/remount; fall back to local state for unexpected client errors.
    const credentialError = loginError || (!requiresTwoFactor && error ? error : '');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);

    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [pinMode, setPinMode] = useState(() => detectPinReturnMode());

    // Load remembered username on mount
    useEffect(() => {
        const savedUsername = localStorage.getItem('rememberedUsername');
        if (savedUsername) {
            setUsername(savedUsername);
            setRememberMe(true);
        }
    }, []);

    const toggleResetModal = () => setShowResetModal(prev => !prev);

    const getDeviceInfo = () => {
        let deviceId = localStorage.getItem('deviceId');
        const deviceName = getDeviceName();

        if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem('deviceId', deviceId);
        }

        return { deviceId, deviceName };
    };

    const handleSubmit = async event => {
        event.preventDefault();
        if (serviceUnavailable) {
            recheck();
            return;
        }
        setLoginError('');
        try {
            const result = await dispatch(login({ username, password }));

            if (result.error) {
                if (result.payload?.requiresTwoFactor) {
                    return;
                }
                // Show the message classified by the thunk (wrong credentials vs. server unreachable)
                setLoginError(typeof result.payload === 'string' ? result.payload : 'Wrong credentials');
            } else {
                // Save or clear remembered username
                if (rememberMe) {
                    localStorage.setItem('rememberedUsername', username);
                } else {
                    localStorage.removeItem('rememberedUsername');
                }
                localStorage.setItem('role', result.payload.user.role);
                localStorage.setItem('hasPinCode', result.payload.user.hasPinCode ? 'true' : 'false');
                toast.success(`Welcome ${username}`);
                navigate('/');
            }
        } catch (error) {
            setLoginError('Wrong credentials');
        }
    };

    const handle2FAVerified = async (verificationData) => {
        try {
            const { deviceId, deviceName } = getDeviceInfo();
            const loginData = {
                ...pendingLogin,
                ...verificationData,
                deviceId,
                deviceName
            };
            
            const result = await dispatch(login(loginData));
            
            if (!result.error) {
                // Set 2FA verification headers so protected endpoints work
                const timestamp = Date.now().toString();
                localStorage.setItem('2fa-verified', 'true');
                localStorage.setItem('2fa-timestamp', timestamp);

                localStorage.setItem('role', result.payload.user.role);
                localStorage.setItem('hasPinCode', result.payload.user.hasPinCode ? 'true' : 'false');
                toast.success(`Welcome ${pendingLogin.username}`);
                dispatch(clearTwoFactorRequirement());
                navigate('/');
            } else {
                errorToast('2FA verification failed');
            }
        } catch (error) {
            errorToast('Error completing login');
        }
    };

    const handle2FACancel = () => {
        dispatch(clearTwoFactorRequirement());
        toast.info('Login cancelled');
    };

    const handleResetSubmit = async event => {
        event.preventDefault();
        try {
            await api.post('/auth/forgot', { email: resetEmail });

            toast.success(`Password reset link sent. Please check your email`);
            setResetEmail('');
            toggleResetModal();
        } catch (error) {
            errorToast('Error sending reset link');
        }
    };

    if (pinMode) {
        return (
            <PinScreen
                onSuccess={() => {
                    toast.success(`Welcome back`);
                    navigate('/');
                }}
                onUsePassword={() => {
                    dispatch(logout());
                    setPinMode(false);
                }}
            />
        );
    }

    return (
        <Container fluid className="login-view bg-broken">
            <Row className="justify-content-center align-items-center min-vh-100">
                <Col className="form-wrap">
                    <div className="text-center">
                        <img src="/logo.svg" alt="Logo" width="220px" />
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Input
                                autoFocus
                                required
                                invalid={!!credentialError}
                                type="text"
                                name="username"
                                id="username"
                                value={username}
                                onChange={e => {
                                    if (credentialError) {
                                        setLoginError('');
                                        dispatch(clearError());
                                    }
                                    setUsername(e.target.value);
                                }}
                            />
                            <Label className="input-text-label" for="username">Username</Label>
                        </FormGroup>

                        <FormGroup>
                            <PasswordInput
                                required
                                invalid={!!credentialError}
                                name="password"
                                id="password"
                                value={password}
                                onChange={e => {
                                    if (credentialError) {
                                        setLoginError('');
                                        dispatch(clearError());
                                    }
                                    setPassword(e.target.value);
                                }}
                            >
                                <Label className="input-text-label" for="password">Password</Label>
                            </PasswordInput>
                        </FormGroup>

                        <FormGroup className="remember-row d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                                <ToggleSwitch isOn={rememberMe} onChange={setRememberMe} />
                                <span className="text-secondary">Remember me</span>
                            </div>

                            <Button color="link" className="key text-decoration-none opacity-50" onClick={toggleResetModal}>
                                <span className="fas fa-lock me-2" /> Forgot password?
                            </Button>
                        </FormGroup>

                        <div className="submit-wrap d-flex flex-column align-items-center">
                            {credentialError && (
                                <div className="login-error">
                                    <span className="fas fa-exclamation-circle me-2" />
                                    {credentialError}
                                </div>
                            )}
                            <Button type="submit" color="primary" outline size="lg" disabled={isLoading || serviceUnavailable}>
                                {isLoading ? (
                                    <>
                                        <span className="fas fa-spinner fa-spin me-2" />
                                        Logging in...
                                    </>
                                ) : serviceUnavailable ? (
                                    'Service unavailable'
                                ) : (
                                    'Log in'
                                )}
                            </Button>
                            <ApiHealthStatus health={health} isChecking={isChecking} onRecheck={recheck} />
                            <div className="version-number">v{Package.version}</div>
                        </div>
                    </Form>
                </Col>
            </Row>

            <Modal isOpen={showResetModal} toggle={toggleResetModal}>
                <ModalHeader toggle={toggleResetModal}>Reset Password</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleResetSubmit}>
                        <FormGroup>
                            <Label for="resetEmail">Email</Label>
                            <Input
                                required
                                type="email"
                                name="resetEmail"
                                id="resetEmail"
                                value={resetEmail}
                                onChange={e => setResetEmail(e.target.value)}
                                placeholder="Enter your email"
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleResetModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleResetSubmit}>
                        Send Reset Link
                    </Button>
                </ModalFooter>
            </Modal>
            
            <TwoFactorVerification 
                isOpen={requiresTwoFactor}
                onVerified={handle2FAVerified}
                onCancel={handle2FACancel}
                purpose="login"
            />
        </Container>
    );
};

export default Login;
